import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ZplLocationSelect } from "@/components/ZplLocationSelect";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { useHubToast } from "@/components/ui/HubToast";
import { ApiError } from "@/lib/api";
import { useHubModuleWriteAny } from "@/lib/useHubModuleWrite";
import { ZPL_PRINTER_STORAGE_KEYS } from "@/lib/zplPrinter";
import { useZplPrintLocation } from "@/lib/zplPrintLocation";
import { EM_DASH } from "@/lib/utils";

import {
  analysisTransfersQueryKey,
  createAnalysisTransfer,
  fetchAnalysisLocations,
  fetchAnalysisTransfers,
  printAnalysisTransferLabel,
  returnAnalysisTransfer,
  type AnalysisTransferParent,
} from "./analysisTransfersApi";

function parentFieldId(parent: AnalysisTransferParent, field: string): string {
  const key =
    parent.rmaId != null
      ? `rma-${parent.rmaId}`
      : parent.qualityCaseId != null
        ? `case-${parent.qualityCaseId}`
        : parent.investigationId != null
          ? `inv-${parent.investigationId}`
          : parent.qualityIncidentId != null
            ? `qi-${parent.qualityIncidentId}`
            : "parent";
  return `analysis-${field}-${key}`;
}

export function QualityAnalysisTransferPanel(props: {
  parent: AnalysisTransferParent;
  defaultSerial?: string;
  embedded?: boolean;
  hideHeading?: boolean;
}) {
  const writesAllowed = useHubModuleWriteAny(["quality", "manufacturing"]);
  const { showToast } = useHubToast();
  const queryClient = useQueryClient();
  const [serial, setSerial] = useState(props.defaultSerial ?? "");
  const [destinationId, setDestinationId] = useState<number | "">("");
  const [notes, setNotes] = useState("");
  const [printOnSend, setPrintOnSend] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const serialId = parentFieldId(props.parent, "serial");
  const destId = parentFieldId(props.parent, "dest");
  const notesId = parentFieldId(props.parent, "notes");
  const printerId = parentFieldId(props.parent, "printer");
  const printOnSendId = parentFieldId(props.parent, "print-on-send");

  const { station, setStation, selectedPrinter, unavailableMessage } = useZplPrintLocation({
    storageKey: ZPL_PRINTER_STORAGE_KEYS.qualityAnalysisLoans,
    labelSize: "4x2",
    dpi: 300,
  });

  const listQuery = useQuery({
    queryKey: analysisTransfersQueryKey(props.parent),
    queryFn: () => fetchAnalysisTransfers(props.parent),
  });
  const locationsQuery = useQuery({
    queryKey: [...analysisTransfersQueryKey(props.parent), "locations"],
    queryFn: fetchAnalysisLocations,
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: analysisTransfersQueryKey(props.parent) });
    await queryClient.invalidateQueries({ queryKey: ["quality", "analysis-transfers"] });
  };

  const sendMutation = useMutation({
    mutationFn: () =>
      createAnalysisTransfer({
        ...props.parent,
        deviceLabelSerial: serial.trim(),
        destinationLocationId: Number(destinationId),
        notes: notes.trim() || null,
        printLabel: printOnSend,
        printer: printOnSend ? selectedPrinter.trim() : undefined,
      }),
    onSuccess: async (result) => {
      setSerial(props.defaultSerial ?? "");
      setNotes("");
      setShowCreate(false);
      if (result.labelPrinted) {
        showToast({ tone: "success", message: "Unit sent for analysis and loan label printed." });
      } else if (printOnSend && result.labelError) {
        showToast({
          tone: "warning",
          message: `Unit sent for analysis, but the loan label did not print: ${result.labelError}`,
        });
      } else {
        showToast({ tone: "success", message: "Unit sent for analysis." });
      }
      await invalidate();
    },
    onError: (error: unknown) => {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : "Could not send the unit.",
      });
    },
  });

  const reprintMutation = useMutation({
    mutationFn: (transferId: number) => printAnalysisTransferLabel(transferId, selectedPrinter.trim()),
    onSuccess: () => {
      showToast({ tone: "success", message: "Loan label sent to the printer." });
    },
    onError: (error: unknown) => {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : "Could not print the loan label.",
      });
    },
  });

  const rows = listQuery.data?.rows ?? [];
  const openRows = rows.filter((row) => row.returnedAt == null);
  const returnedRows = rows.filter((row) => row.returnedAt != null);
  const printerReady = selectedPrinter.trim() !== "";
  const sendDisabled =
    serial.trim() === "" ||
    destinationId === "" ||
    sendMutation.isPending ||
    (printOnSend && !printerReady);

  const body = (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        Send a unit to TenX, a contract manufacturer, or another location for analysis. This is not a Megaventory
        transfer. Record the loan on the RCA investigation, a quality case, or a QC incident. Optional 4x2 loan labels
        use the standard equipment layout (serial, destination, case link, notes) plus an ECC200 link to this loan on the hub.
      </p>
      {openRows.length === 0 ? (
        <p className="text-sm text-slate-600">No units are out for analysis on this record.</p>
      ) : (
        <ul className="space-y-2 text-sm">
          {openRows.map((row) => (
            <li key={row.id} className="flex flex-wrap items-center gap-2">
              <Link className="font-mono text-sky-800 hover:underline" to={`/quality/analysis-loans/${row.id}`}>
                {row.deviceLabelSerial}
              </Link>
              <span>at {row.destinationName ?? EM_DASH}</span>
              {writesAllowed ? (
                <>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!printerReady || reprintMutation.isPending}
                    onClick={() => reprintMutation.mutate(row.id)}
                  >
                    Print label
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      void returnAnalysisTransfer(row.id).then(async () => {
                        showToast({ tone: "success", message: "Marked returned." });
                        await invalidate();
                      })
                    }
                  >
                    Mark returned
                  </Button>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {returnedRows.length > 0 ? (
        <p className="text-xs text-slate-500">
          Returned: {returnedRows.map((row) => row.deviceLabelSerial).join(", ")}
        </p>
      ) : null}
      {writesAllowed && !showCreate ? (
        <Button size="sm" variant="secondary" onClick={() => setShowCreate(true)}>
          New analysis loan
        </Button>
      ) : null}
      {writesAllowed && showCreate ? (
        <div className="space-y-3">
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label htmlFor={serialId}>Serial</Label>
              <Input id={serialId} value={serial} onChange={(event) => setSerial(event.target.value)} />
            </div>
            <div>
              <Label htmlFor={destId}>Destination</Label>
              <select
                id={destId}
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                value={destinationId === "" ? "" : String(destinationId)}
                onChange={(event) => setDestinationId(event.target.value === "" ? "" : Number(event.target.value))}
              >
                <option value="">Select location</option>
                {(locationsQuery.data?.rows ?? []).map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor={notesId}>Notes</Label>
              <Input id={notesId} value={notes} onChange={(event) => setNotes(event.target.value)} />
            </div>
            <div>
              <ZplLocationSelect
                id={printerId}
                value={station}
                onChange={setStation}
                unavailableMessage={unavailableMessage}
                disabled={sendMutation.isPending}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
              <input
                id={printOnSendId}
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300"
                checked={printOnSend}
                onChange={(event) => setPrintOnSend(event.target.checked)}
              />
              Print 4x2 loan label when sending
            </label>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button disabled={sendDisabled} onClick={() => sendMutation.mutate()}>
              Send
            </Button>
          </div>
        </div>
      ) : null}
      <p className="text-xs text-slate-500">
        <Link className="text-sky-800 hover:underline" to="/quality/analysis-loans">
          All open analysis loans
        </Link>
      </p>
    </div>
  );

  if (props.embedded) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3">
        {props.hideHeading ? null : <p className="mb-2 text-sm font-semibold text-slate-800">Analysis loans</p>}
        {body}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Analysis loans</CardTitle>
      </CardHeader>
      <CardBody>{body}</CardBody>
    </Card>
  );
}
